import React, { useState, useMemo } from 'react';
import { Box, Modal, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonPagination from '../../../../form-components/CommonPagination';
import { assignTicketToClasses } from '../../../../api/ticket';

const DAY_LABELS = { 0: '일', 1: '월', 2: '화', 3: '수', 4: '목', 5: '금', 6: '토' };
const PAGE_SIZE = 15;

const ClassSelectModal = ({ open, onClose, availableClasses = [], newTicketId, user, toast, onDone }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterClassName, setFilterClassName] = useState('');
  const [filterDayOfWeek, setFilterDayOfWeek] = useState('');
  const [filterTrainer, setFilterTrainer] = useState('');
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);

  // 강사 목록 추출
  const trainerOptions = useMemo(() => {
    const map = {};
    availableClasses.forEach(c => {
      if (c.trainerName && c.trainerId) map[c.trainerId] = c.trainerName;
    });
    return Object.entries(map).map(([v, l]) => ({ value: v, label: l }));
  }, [availableClasses]);

  // 필터
  const filtered = useMemo(() => {
    return availableClasses.filter(c => {
      if (filterClassName && !c.className?.toLowerCase().includes(filterClassName.toLowerCase())) return false;
      if (filterDayOfWeek !== '' && c.classDate) {
        const day = new Date(c.classDate).getDay();
        if (day !== Number(filterDayOfWeek)) return false;
      }
      if (filterTrainer && String(c.trainerId) !== String(filterTrainer)) return false;
      return true;
    });
  }, [availableClasses, filterClassName, filterDayOfWeek, filterTrainer]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const allSelected = filtered.length > 0 && filtered.every(c => selectedIds.includes(c.id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(filtered.map(c => c.id));
  };

  const toggleOne = id => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    if (selectedIds.length > 0 && newTicketId) {
      setSaving(true);
      try {
        await assignTicketToClasses(newTicketId, user.branch?.branchId, selectedIds, user.token);
        toast.success(`${selectedIds.length}개 수업에 수강권이 포함되었습니다`);
      } catch (e) {
        toast.error('수업 매핑에 실패했습니다');
      }
      setSaving(false);
    }
    onDone();
  };

  const S = {
    overlay: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '900px', height: '80vh', background: '#fff', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden', outline: 'none' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px', background: '#333', color: '#fff' },
    headerTitle: { fontSize: '16px', fontWeight: 700 },
    headerSub: { fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '2px' },
    filterRow: { display: 'flex', gap: '8px', padding: '12px 24px', borderBottom: '1px solid #F0F0F0', alignItems: 'center', flexWrap: 'wrap' },
    filterInput: { height: '34px', padding: '0 10px', border: '1px solid #D9D9D9', borderRadius: '6px', fontSize: '13px', outline: 'none', fontFamily: 'Pretendard, sans-serif' },
    filterSelect: { height: '34px', padding: '0 8px', border: '1px solid #D9D9D9', borderRadius: '6px', fontSize: '13px', outline: 'none', cursor: 'pointer', fontFamily: 'Pretendard, sans-serif', appearance: 'none', WebkitAppearance: 'none', paddingRight: '24px', background: '#fff' },
    tableHead: { display: 'flex', padding: '8px 24px', background: '#F7F7F7', borderBottom: '1px solid #EAEAEA', fontSize: '12px', fontWeight: 600, color: '#888' },
    tableRow: (checked) => ({ display: 'flex', alignItems: 'center', padding: '9px 24px', borderBottom: '1px solid #F5F5F5', fontSize: '13px', color: '#333', cursor: 'pointer', background: checked ? '#F5FAFA' : '#fff', transition: 'background 0.1s' }),
    footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 24px', borderTop: '1px solid #E8E8E8' },
  };

  return (
    <Modal open={open} onClose={onClose} disableScrollLock>
      <Box sx={S.overlay}>
        {/* 헤더 */}
        <Box sx={S.header}>
          <Box>
            <Box sx={S.headerTitle}>수업에 수강권 포함</Box>
            <Box sx={S.headerSub}>이 수강권을 포함시킬 그룹레슨 수업을 선택해주세요</Box>
          </Box>
          <CloseIcon sx={{ cursor: 'pointer', color: '#fff', fontSize: 20 }} onClick={onClose} />
        </Box>

        {/* 필터 */}
        <Box sx={S.filterRow}>
          <input
            placeholder="수업명 검색"
            value={filterClassName}
            onChange={e => { setFilterClassName(e.target.value); setPage(1); }}
            style={{ ...S.filterInput, width: '180px' }}
          />
          <select
            value={filterDayOfWeek}
            onChange={e => { setFilterDayOfWeek(e.target.value); setPage(1); }}
            style={S.filterSelect}
          >
            <option value="">전체 요일</option>
            {Object.entries(DAY_LABELS).map(([v, l]) => <option key={v} value={v}>{l}요일</option>)}
          </select>
          <select
            value={filterTrainer}
            onChange={e => { setFilterTrainer(e.target.value); setPage(1); }}
            style={S.filterSelect}
          >
            <option value="">전체 강사</option>
            {trainerOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ fontSize: '13px', color: '#4AB3BC', fontWeight: 600 }}>{selectedIds.length}개 선택</Box>
            <Box sx={{ fontSize: '12px', color: '#999' }}>/ {filtered.length}개</Box>
          </Box>
        </Box>

        {/* 테이블 */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Box sx={S.tableHead}>
            <Box sx={{ width: '40px' }}>
              <Checkbox checked={allSelected} onChange={toggleAll} size="small"
                sx={{ padding: 0, color: '#A6A6A6', '&.Mui-checked': { color: '#4AB3BC' } }} />
            </Box>
            <Box sx={{ width: '100px' }}>수업일</Box>
            <Box sx={{ width: '50px' }}>요일</Box>
            <Box sx={{ width: '110px' }}>시간</Box>
            <Box sx={{ flex: 1 }}>수업명</Box>
            <Box sx={{ width: '90px' }}>강사</Box>
            <Box sx={{ width: '60px', textAlign: 'center' }}>정원</Box>
          </Box>
          {paged.map(cls => {
            const checked = selectedIds.includes(cls.id);
            const dayOfWeek = cls.classDate ? DAY_LABELS[new Date(cls.classDate).getDay()] : '';
            return (
              <Box key={cls.id} onClick={() => toggleOne(cls.id)} sx={S.tableRow(checked)}>
                <Box sx={{ width: '40px' }}>
                  <Checkbox checked={checked} size="small" readOnly
                    sx={{ padding: 0, color: '#D9D9D9', '&.Mui-checked': { color: '#4AB3BC' } }} />
                </Box>
                <Box sx={{ width: '100px' }}>{cls.classDate}</Box>
                <Box sx={{ width: '50px' }}>{dayOfWeek}</Box>
                <Box sx={{ width: '110px' }}>{cls.startTime?.substring(0, 5)}~{cls.endTime?.substring(0, 5)}</Box>
                <Box sx={{ flex: 1, fontWeight: 500 }}>{cls.className}</Box>
                <Box sx={{ width: '90px', color: '#666' }}>{cls.trainerName || '-'}</Box>
                <Box sx={{ width: '60px', textAlign: 'center', color: '#666' }}>{cls.maxCount || '-'}명</Box>
              </Box>
            );
          })}
          {filtered.length === 0 && (
            <Box sx={{ padding: '40px', textAlign: 'center', color: '#999', fontSize: '13px' }}>매핑 가능한 그룹레슨 수업이 없습니다</Box>
          )}
        </Box>

        {/* 페이지네이션 + 푸터 */}
        <Box sx={S.footer}>
          <CommonPagination
            totalElements={filtered.length}
            limit={PAGE_SIZE}
            page={page}
            onChange={(_, v) => setPage(v)}
          />
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <CommonModalButton variant="cancel" onClick={onClose}>건너뛰기</CommonModalButton>
            <CommonModalButton variant="submit" onClick={handleSave} disabled={saving}>
              {selectedIds.length > 0 ? `${selectedIds.length}개 수업에 포함` : '건너뛰기'}
            </CommonModalButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ClassSelectModal;
